import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import toast from 'react-hot-toast';
import { saveAs } from 'file-saver';
import {
  Eye,
  Download,
  Upload,
  Plus,
  Users,
  TrendingUp,
  TrendingDown,
  Building2,
  Calendar,
  Clock,
  ListChecks,
  XCircle,
  AlertCircle,
  Send,
  UserCheck,
  ArrowUpRight,
  Trash2,
  Search,
  MessageCircle,
  Pencil
} from 'lucide-react';
import EditPreleadModal from '../components/prelead/EditPreleadModal';
import { fetchEmailLeadProjectId, emailLeadPortal } from '../utils/emailLeadPrelead';
import { useNavigate, useLocation } from 'react-router-dom';
import Modal from '../../shared/components/ui/Modal';
import Button from '../../shared/components/ui/Button';
import Loader from '../../shared/components/ui/Loader';
import PaginationComponent from '../../shared/components/PaginationComponent';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import UploadResultsModal from '../components/ui/UploadResultsModal';
import PreleadUpdateStatusModal from '../components/ui/PreleadUpdateStatusModal';
import PreleadLogModal, { PreleadNoteButton } from '../components/ui/PreleadLogModal';
import { normalizeTemplateUploadResponse } from '../utils/templateUploadResponse';
import { normalizeLeadPhone } from '../utils/leadWhatsAppUtils';
import {
  addPreLeadNote,
  createLead,
  createPreLeadList,
  deletePreLeadList,
  downloadPreLeadTemplate,
  getAllCompanies,
  getPendingTelecalling,
  getPreLeadGoogleAdsFields,
  getPreLeadWebsiteFormFields,
  getPreLeadEmailLeadFields,
  getPreLeadLists,
  getPreLeadMetaFields,
  markPreLeadAsUnqualified,
  uploadPreLeadTemplate
} from '../services/api/implementation/impl';
import {
  formatPreleadStatusNote,
  PRELEAD_STATUS_CATEGORY
} from '../utils/preleadStatusOptions';
import { capitalizeWords, formatStatusLabel } from '../utils/stringUtils';
import WhatsAppFlowSendStatusChip from '../components/prelead/WhatsAppFlowSendStatusChip';
import PreleadSlaIndicator from '../components/prelead/PreleadSlaIndicator';

function formatListDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function getPreleadNormalizedPhone(row) {
  const combined = [row?.countryCode, row?.phone].filter(Boolean).join(' ').trim();
  return normalizeLeadPhone(combined);
}

function resolvePendingTelecallingProjectId(row) {
  const candidates = [
    row?.project_id,
    row?.whatsapp_flow_project_id,
    row?.interested_campaign?.project_id,
    row?.interested_campaign?.project?.id,
    row?.interested_campaign?.WhatsAppCampaign?.project_id
  ];

  const interestedCampaignSend = (row?.whatsapp_campaign_sends ?? [])
    .filter((send) => String(send?.state ?? '').trim().toLowerCase() === 'interested')
    .sort((a, b) => new Date(b?.sent_at ?? 0) - new Date(a?.sent_at ?? 0))[0];
  candidates.push(interestedCampaignSend?.WhatsAppCampaign?.project_id);

  for (const value of candidates) {
    const projectId = Number(value);
    if (Number.isFinite(projectId) && projectId > 0) return projectId;
  }
  return null;
}

/** EmailLeadPortal enum values, as written to prelead.type by email ingestion. */
const EMAIL_LEAD_PORTAL_TYPES = [
  '99acres',
  'housing',
  'nobroker',
  'magicbricks',
  'squareyards'
];

function getPendingTelecallingSource(prelead) {
  return (
    prelead?.type ||
    prelead?.source ||
    prelead?.pre_lead_list?.source ||
    ''
  );
}

function getAnswerLabel(answer) {
  if (!answer) return '—';
  return (
    answer?.questionOption?.label ||
    answer?.question_option?.label ||
    answer?.raw_text ||
    answer?.raw_payload ||
    '—'
  );
}

function getQuestionOptionLabels(answer) {
  const optionBuckets = [
    answer?.question?.options,
    answer?.question?.questionOptions,
    answer?.question?.question_options,
    answer?.question_options,
    answer?.options
  ];

  const flattenedOptions = optionBuckets.flatMap((bucket) => (Array.isArray(bucket) ? bucket : []));
  const labels = flattenedOptions
    .map((option) =>
      String(
        option?.label ?? option?.option_label ?? option?.text ?? option?.title ?? option?.value ?? ''
      ).trim()
    )
    .filter(Boolean);

  return Array.from(new Set(labels));
}

function formatMetaFieldQuestion(question) {
  if (!question) return '—';
  return String(question)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function PreleadFormSummaryCard({
  prelead,
  formMeta,
  formMetaLabel = 'Form',
  showWhatsAppAutoSend = false
}) {
  const email = prelead?.email?.trim() || '—';
  const status = prelead?.state ? formatStatusLabel(String(prelead.state)) : '—';
  const formName = formMeta?.name || null;
  const projectName = formMeta?.project_name?.trim() || null;

  if (!prelead && !formMeta) return null;

  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50/80 px-3 py-2.5">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
          {formMetaLabel}
        </span>
        {formName && <span className="text-sm font-medium text-gray-900">{formName}</span>}
        {projectName && <span className="text-xs text-gray-500">· {projectName}</span>}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        <span className="text-gray-700">
          <span className="text-gray-400">Email </span>
          <span className="font-medium text-gray-900 break-all">{email}</span>
        </span>
        <span className="text-gray-700">
          <span className="text-gray-400">Status </span>
          <span className="font-medium text-gray-900">{status}</span>
        </span>
        {showWhatsAppAutoSend && (
          <span className="inline-flex items-center gap-1.5 text-gray-700">
            <span className="text-gray-400">WA Auto-send</span>
            <WhatsAppFlowSendStatusChip row={prelead} />
          </span>
        )}
      </div>
    </div>
  );
}

function FacebookMetaFieldsModal({ preLeadId, prelead, onClose }) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['preLeadMetaFields', preLeadId],
    queryFn: () => getPreLeadMetaFields(preLeadId),
    enabled: Number.isFinite(preLeadId) && preLeadId > 0
  });

  const fields = data?.fields ?? [];
  const form = data?.form;

  return (
    <Modal width={50} name="Facebook Form Responses" onClose={onClose}>
      <div className="max-h-[70vh] overflow-y-auto space-y-4">
        {(prelead || form || isLoading) && (
          <PreleadFormSummaryCard
            prelead={prelead}
            formMeta={form}
            formMetaLabel="Form"
            showWhatsAppAutoSend
          />
        )}
        {isLoading && <p className="text-sm text-gray-500">Loading form responses…</p>}
        {isError && (
          <p className="text-sm text-red-700">
            {error?.response?.data?.message || error?.message || 'Could not load form responses.'}
          </p>
        )}
        {!isLoading && !isError && (
          <>
            {fields.length > 0 ? (
              fields.map((field, index) => (
                <div
                  key={field.id ?? `${field.question}-${index}`}
                  className="rounded-xl border border-gray-200 bg-white p-4 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
                  <div className="flex items-start gap-2">
                    <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-customBlue/10 px-1.5 text-[11px] font-semibold text-customBlue">
                      {index + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-gray-500">Field {index + 1}</p>
                      <p className="mt-1 text-sm font-medium text-gray-900">
                        {formatMetaFieldQuestion(field.question)}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50/70 px-3 py-2">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Answer</p>
                    <p className="mt-1 text-sm font-medium text-emerald-900 break-words">
                      {field.answer || '—'}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500">No form fields available.</p>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

function GoogleAdsFieldsModal({ preLeadId, prelead, onClose }) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['preLeadGoogleAdsFields', preLeadId],
    queryFn: () => getPreLeadGoogleAdsFields(preLeadId),
    enabled: Number.isFinite(preLeadId) && preLeadId > 0
  });

  const fields = data?.fields ?? [];
  const source = data?.source;

  return (
    <Modal width={50} name="Google Ads Form Responses" onClose={onClose}>
      <div className="max-h-[70vh] overflow-y-auto space-y-4">
        {(prelead || source || isLoading) && (
          <PreleadFormSummaryCard prelead={prelead} formMeta={source} formMetaLabel="Source" />
        )}
        {isLoading && <p className="text-sm text-gray-500">Loading form responses…</p>}
        {isError && (
          <p className="text-sm text-red-700">
            {error?.response?.data?.message || error?.message || 'Could not load form responses.'}
          </p>
        )}
        {!isLoading && !isError && (
          <>
            {fields.length > 0 ? (
              fields.map((field, index) => (
                <div
                  key={field.id ?? `${field.question}-${index}`}
                  className="rounded-xl border border-gray-200 bg-white p-4 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
                  <div className="flex items-start gap-2">
                    <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-customBlue/10 px-1.5 text-[11px] font-semibold text-customBlue">
                      {index + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-gray-500">Field {index + 1}</p>
                      <p className="mt-1 text-sm font-medium text-gray-900">
                        {formatMetaFieldQuestion(field.question)}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50/70 px-3 py-2">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Answer</p>
                    <p className="mt-1 text-sm font-medium text-emerald-900 break-words">
                      {field.answer || '—'}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500">No form fields available.</p>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

function WebsiteFormFieldsModal({ preLeadId, prelead, onClose }) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['preLeadWebsiteFormFields', preLeadId],
    queryFn: () => getPreLeadWebsiteFormFields(preLeadId),
    enabled: Number.isFinite(preLeadId) && preLeadId > 0
  });
  const fields = data?.fields ?? [];
  const source = data?.source;

  return (
    <Modal width={50} name="Website / Framer Form Responses" onClose={onClose}>
      <div className="max-h-[70vh] overflow-y-auto space-y-4">
        {(prelead || source || isLoading) && (
          <PreleadFormSummaryCard prelead={prelead} formMeta={source} formMetaLabel="Form" />
        )}
        {isLoading && <p className="text-sm text-gray-500">Loading form responses…</p>}
        {isError && (
          <p className="text-sm text-red-700">
            {error?.response?.data?.message || error?.message || 'Could not load form responses.'}
          </p>
        )}
        {!isLoading && !isError && (
          fields.length > 0 ? (
            fields.map((field, index) => (
              <div
                key={field.id ?? `${field.question}-${index}`}
                className="rounded-xl border border-gray-200 bg-white p-4 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
                <p className="text-xs font-medium text-gray-500">Field {index + 1}</p>
                <p className="mt-1 text-sm font-medium text-gray-900">
                  {formatMetaFieldQuestion(field.question)}
                </p>
                <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50/70 px-3 py-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Answer</p>
                  <p className="mt-1 text-sm font-medium text-emerald-900 break-words">
                    {field.answer || '—'}
                  </p>
                </div>
              </div>
            ))
          ) : (
            <p className="text-sm text-gray-500">No form fields available.</p>
          )
        )}
      </div>
    </Modal>
  );
}

/**
 * What the portal sent, for a lead that arrived by email.
 *
 * Budget, locality, configuration and the enquirer's own message all come in
 * the portal's email and have no column on pre_leads, so they are stored as
 * label/value pairs and rendered here — the same shape and the same component
 * treatment as the Google Ads and website form responses beside it.
 */
function EmailLeadFieldsModal({ preLeadId, prelead, onClose }) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['preLeadEmailLeadFields', preLeadId],
    queryFn: () => getPreLeadEmailLeadFields(preLeadId),
    enabled: Number.isFinite(preLeadId) && preLeadId > 0
  });
  const fields = data?.fields ?? [];
  const source = data?.source;

  return (
    <Modal width={50} name="Portal Email Enquiry" onClose={onClose}>
      <div className="max-h-[70vh] overflow-y-auto space-y-4">
        {(prelead || source || isLoading) && (
          <PreleadFormSummaryCard prelead={prelead} formMeta={source} formMetaLabel="Source" />
        )}
        {data?.subject && (
          <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              Original email
            </p>
            <p className="mt-1 text-sm font-medium text-gray-900 break-words">{data.subject}</p>
            <p className="mt-0.5 text-xs text-gray-600 break-all">
              {data.from_address}
              {data.received_at ? ` · ${new Date(data.received_at).toLocaleString()}` : ''}
            </p>
          </div>
        )}
        {isLoading && <p className="text-sm text-gray-500">Loading enquiry details…</p>}
        {isError && (
          <p className="text-sm text-red-700">
            {error?.response?.data?.message || error?.message || 'Could not load enquiry details.'}
          </p>
        )}
        {!isLoading && !isError && (
          fields.length > 0 ? (
            fields.map((field, index) => (
              <div
                key={field.id ?? `${field.question}-${index}`}
                className="rounded-xl border border-gray-200 bg-white p-4 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
                <p className="mt-1 text-sm font-medium text-gray-900">
                  {formatMetaFieldQuestion(field.question)}
                </p>
                <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50/70 px-3 py-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">
                    Value
                  </p>
                  <p className="mt-1 text-sm font-medium text-emerald-900 break-words">
                    {field.answer || '—'}
                  </p>
                </div>
              </div>
            ))
          ) : (
            <p className="text-sm text-gray-500">
              This lead came from a portal email, but the email carried no extra details beyond
              the contact itself.
            </p>
          )
        )}
      </div>
    </Modal>
  );
}

function WhatsAppFlowAnswersModal({ selectedPrelead, onClose }) {
  const answers = selectedPrelead?.whatsapp_flow_question_answers ?? [];

  return (
    <Modal width={50} name="WhatsApp Flow Responses" onClose={onClose}>
      <div className="max-h-[70vh] overflow-y-auto space-y-4">
        {answers.length > 0 ? (
          answers
            .slice()
            .sort((a, b) => (a?.question?.question_order ?? 999) - (b?.question?.question_order ?? 999))
            .map((answer, index) => {
              const selectedAnswer = getAnswerLabel(answer);
              const selectedAnswerNorm = selectedAnswer.toLowerCase().trim();
              const otherOptions = getQuestionOptionLabels(answer).filter(
                (label) => label.toLowerCase().trim() !== selectedAnswerNorm
              );

              return (
                <div
                  key={answer.id ?? `${answer.question_id}-${index}`}
                  className="rounded-xl border border-gray-200 bg-white p-4 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
                  <div className="flex items-start gap-2">
                    <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-customBlue/10 px-1.5 text-[11px] font-semibold text-customBlue">
                      {index + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-gray-500">Question {index + 1}</p>
                      <p className="mt-1 text-sm font-medium text-gray-900">
                        {answer?.question?.prompt_text || '—'}
                      </p>
                    </div>
                  </div>

                  <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50/70 px-3 py-2">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">
                      Selected Answer
                    </p>
                    <p className="mt-1 text-sm font-medium text-emerald-900">{selectedAnswer}</p>
                  </div>

                  <div className="mt-3">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-customBlue">
                      Other Options
                    </p>
                    {otherOptions.length > 0 ? (
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {otherOptions.map((optionLabel) => (
                          <span
                            key={optionLabel}
                            className="inline-flex rounded-full border border-customBlue/20 bg-customBlue/5 px-2.5 py-1 text-xs text-customBlue">
                            {optionLabel}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="mt-1 text-xs text-gray-500">No other options captured.</p>
                    )}
                  </div>
                </div>
              );
            })
        ) : (
          <p className="text-sm text-gray-500">No WhatsApp flow answers available.</p>
        )}
      </div>
    </Modal>
  );
}

const RESPONSE_BUTTON_CLASS =
  'inline-flex items-center rounded-lg border border-customBlue/40 bg-customBlue/5 px-2.5 py-1 text-[11px] font-semibold text-customBlue hover:bg-customBlue/10 transition-colors';

function renderPendingTelecallingResponseCell(prelead, handlers) {
  const source = String(getPendingTelecallingSource(prelead)).trim().toLowerCase();
  const row = {
    ...prelead,
    lead_id: prelead.lead?.id ?? null
  };

  if (source === 'facebook') {
    return (
      <button type="button" onClick={() => handlers.onFacebook(row)} className={RESPONSE_BUTTON_CLASS}>
        View Form
      </button>
    );
  }

  if (source === 'google_ads') {
    return (
      <button type="button" onClick={() => handlers.onGoogleAds(row)} className={RESPONSE_BUTTON_CLASS}>
        View Form
      </button>
    );
  }

  if (source === 'website') {
    return (
      <button type="button" onClick={() => handlers.onWebsite(row)} className={RESPONSE_BUTTON_CLASS}>
        View Form
      </button>
    );
  }

  // Matches the EmailLeadPortal enum values written to prelead.type by portal
  // email ingestion. "View Enquiry" rather than "View Form" because there was
  // no form — the buyer's details arrived in an email.
  if (EMAIL_LEAD_PORTAL_TYPES.includes(source)) {
    return (
      <button type="button" onClick={() => handlers.onEmailLead(row)} className={RESPONSE_BUTTON_CLASS}>
        View Enquiry
      </button>
    );
  }

  if (Array.isArray(prelead.whatsapp_flow_question_answers) && prelead.whatsapp_flow_question_answers.length > 0) {
    return (
      <button type="button" onClick={() => handlers.onWhatsAppFlow(row)} className={RESPONSE_BUTTON_CLASS}>
        View Q&A
      </button>
    );
  }

  return <span className="text-gray-400 text-xs">—</span>;
}

const PreleadList = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { collapsed } = useSidebar();
  const isMarketing = location.pathname.startsWith('/builder/marketing');
  const userVertical = Cookies.get('userVertical');
  const userDesignationLevel = Cookies.get('userDesignationLevel');
  const canDeletePreleads =
    userVertical === 'Management' ||
    (userVertical === 'Sales' &&
      (userDesignationLevel === 'Medium' || userDesignationLevel === 'High'));
  const initialCompanyId =
    localStorage.getItem('companyId') || Cookies.get('companyId') || Cookies.get('company_id') || '';

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [companyId, setCompanyId] = useState(String(initialCompanyId || ''));
  const [listName, setListName] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const quickUploadInputRef = useRef(null);
  const [quickUploadListId, setQuickUploadListId] = useState(null);
  const lastUploadListIdRef = useRef(null);

  const [isUploadResultModalOpen, setIsUploadResultModalOpen] = useState(false);
  const [uploadErrors, setUploadErrors] = useState([]);
  const [uploadSuccesses, setUploadSuccesses] = useState([]);
  const [uploadDuplicates, setUploadDuplicates] = useState([]);
  const [pendingDeleteList, setPendingDeleteList] = useState(null);

  const [telecallingCompanyId, setTelecallingCompanyId] = useState(String(initialCompanyId || ''));
  const [telecallingPage, setTelecallingPage] = useState(1);
  const [telecallingSearchInput, setTelecallingSearchInput] = useState('');
  const [telecallingSearch, setTelecallingSearch] = useState('');
  const [selectedPreleadForLogs, setSelectedPreleadForLogs] = useState(null);
  const [selectedPreleadForAnswers, setSelectedPreleadForAnswers] = useState(null);
  const [selectedPreleadForMetaFields, setSelectedPreleadForMetaFields] = useState(null);
  const [selectedPreleadForGoogleAdsFields, setSelectedPreleadForGoogleAdsFields] = useState(null);
  const [selectedPreleadForWebsiteFields, setSelectedPreleadForWebsiteFields] = useState(null);
  const [selectedPreleadForEmailLeadFields, setSelectedPreleadForEmailLeadFields] = useState(null);
  const [statusUpdateRow, setStatusUpdateRow] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [activeListTab, setActiveListTab] = useState('all-lists'); // 'all-lists' | 'pending-telecalling'

  const { data: companies = [], isPending: isCompaniesPending } = useQuery({
    queryKey: ['allCompaniesForPreleadList'],
    queryFn: getAllCompanies
  });

  const {
    data: listsData = { overall: null, preLeadList: [] },
    isLoading: isListsLoading,
    isError: isListsError,
    error: listsError
  } = useQuery({
    queryKey: ['preLeadLists'],
    queryFn: getPreLeadLists
  });

  const {
    data: pendingTelecallingData = { preLeads: [], meta: { page: 1, limit: 20, total: 0, totalPages: 1 } },
    isLoading: isPendingTelecallingLoading,
    isFetching: isPendingTelecallingFetching,
    isError: isPendingTelecallingError,
    error: pendingTelecallingError
  } = useQuery({
    queryKey: ['pendingTelecalling', telecallingCompanyId, telecallingPage, telecallingSearch],
    queryFn: () =>
      getPendingTelecalling({
        company_id: telecallingCompanyId,
        page: telecallingPage,
        limit: 20,
        search: telecallingSearch
      }),
    enabled: Boolean(telecallingCompanyId),
    keepPreviousData: true
  });

  const lists = listsData.preLeadList;
  const overallStats = listsData.overall;
  const pendingTelecallingRows = pendingTelecallingData.preLeads ?? [];
  const pendingTelecallingMeta = pendingTelecallingData.meta ?? {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1
  };

  const companyNameById = useMemo(() => {
    const map = new Map();
    companies.forEach((c) => {
      map.set(c.id, c.company_name || c.name || `Company ${c.id}`);
      map.set(String(c.id), c.company_name || c.name || `Company ${c.id}`);
    });
    return map;
  }, [companies]);

  const sortedLists = useMemo(
    () =>
      [...lists].sort((a, b) => {
        const ta = new Date(a.created_at).getTime();
        const tb = new Date(b.created_at).getTime();
        if (tb !== ta) return tb - ta;
        return (b.id ?? 0) - (a.id ?? 0);
      }),
    [lists]
  );

  useEffect(() => {
    if (companyId || companies.length === 0) return;
    const firstId = String(companies[0].id);
    setCompanyId(firstId);
  }, [companies, companyId]);

  useEffect(() => {
    if (telecallingCompanyId || companies.length === 0) return;
    setTelecallingCompanyId(String(companies[0].id));
  }, [companies, telecallingCompanyId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const next = telecallingSearchInput.trim();
      setTelecallingSearch((prev) => {
        if (prev !== next) {
          setTelecallingPage(1);
        }
        return next;
      });
    }, 350);
    return () => clearTimeout(timer);
  }, [telecallingSearchInput]);

  const { mutate: submitPreLeadList, isPending: isCreatePending } = useMutation({
    mutationFn: (payload) => createPreLeadList(payload),
    onSuccess: () => {
      toast.success('List created successfully');
      setIsCreateModalOpen(false);
      setListName('');
      queryClient.invalidateQueries({ queryKey: ['preLeadLists'] });
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to create list');
    }
  });

  const { mutate: removePreLeadList, isPending: isDeletePending } = useMutation({
    mutationFn: (listId) => deletePreLeadList(listId),
    onSuccess: (_data, listId) => {
      toast.success('List deleted');
      setPendingDeleteList(null);
      const removedId = Number(listId);
      queryClient.setQueryData(['preLeadLists'], (old) => {
        if (!old || !Array.isArray(old.preLeadList)) return old;
        return {
          ...old,
          preLeadList: old.preLeadList.filter((list) => Number(list?.id) !== removedId)
        };
      });
      queryClient.removeQueries({ queryKey: ['preLeadsByListId', removedId] });
      queryClient.invalidateQueries({ queryKey: ['preLeadLists'] });
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to delete list');
    }
  });

  const runDownload = async (listId, safeName) => {
    setDownloadingId(listId);
    try {
      const buffer = await downloadPreLeadTemplate(listId);
      if (!buffer || (buffer.byteLength !== undefined && buffer.byteLength === 0)) {
        toast.error('Downloaded file is empty.');
        return;
      }
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const slug = (safeName || `list_${listId}`).replace(/[^a-zA-Z0-9-_]/g, '_');
      saveAs(blob, `Prelead_${slug}_template.xlsx`);
      toast.success('Template downloaded');
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Download failed');
    } finally {
      setDownloadingId(null);
    }
  };

  const runUpload = async (listId, file) => {
    if (!file) return;
    lastUploadListIdRef.current = listId;
    setUploadingId(listId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await uploadPreLeadTemplate(listId, formData);
      const { errors, results, duplicates } = normalizeTemplateUploadResponse(response);

      if (errors.length > 0) {
        setUploadErrors(errors);
        setUploadSuccesses(results);
        setUploadDuplicates(duplicates);
        setIsUploadResultModalOpen(true);
        queryClient.invalidateQueries({ queryKey: ['preLeadLists'] });
        queryClient.invalidateQueries({ queryKey: ['preLeadsByListId', listId] });
      } else {
        toast.success('Template uploaded successfully');
        queryClient.invalidateQueries({ queryKey: ['preLeadLists'] });
        queryClient.invalidateQueries({ queryKey: ['preLeadsByListId', listId] });
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Upload failed');
    } finally {
      setUploadingId(null);
    }
  };

  const closeUploadResultModal = () => {
    setIsUploadResultModalOpen(false);
    setUploadErrors([]);
    setUploadSuccesses([]);
    setUploadDuplicates([]);
  };

  const handleRejectPreleadDuplicates = (duplicatesToReject) => {
    setUploadDuplicates((prev) =>
      prev.filter((item) => !duplicatesToReject.some((rejected) => rejected.row === item.row))
    );
    if (
      uploadDuplicates.length === duplicatesToReject.length &&
      uploadErrors.length === 0
    ) {
      closeUploadResultModal();
    }
  };

  const handleRetryPreleadUpload = () => {
    const id = lastUploadListIdRef.current;
    closeUploadResultModal();
    if (id != null) {
      openQuickUploadPicker(id);
    }
  };

  const openQuickUploadPicker = (listId) => {
    setQuickUploadListId(listId);
    requestAnimationFrame(() => quickUploadInputRef.current?.click());
  };

  const onQuickUploadChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !quickUploadListId) return;
    runUpload(quickUploadListId, file);
    setQuickUploadListId(null);
  };

  const openListDetailPage = (list, { filter } = {}) => {
    if (!isMarketing) {
      try {
        localStorage.setItem('salesMainSecondaryTab', 'Prelead List');
      } catch {
        /* ignore */
      }
    }
    const base = isMarketing ? '/builder/marketing' : '/builder/sales';
    const filterQuery = filter ? `?filter=${encodeURIComponent(filter)}` : '';
    navigate(`${base}/prelead-lists/${list.id}${filterQuery}`, {
      state: {
        listName: list.name,
        companyId: list.company_id,
        createdAt: list.created_at,
        source: list.source
      }
    });
  };

  const openPendingTelecallingList = (prelead) => {
    const list = prelead?.pre_lead_list;
    if (!list?.id) {
      toast.error('List not found for this prelead.');
      return;
    }
    openListDetailPage(list, { filter: 'pending-action' });
  };

  const toPendingTelecallingRow = (prelead) => ({
    ...prelead,
    pre_lead_id: prelead.id,
    lead_id: prelead.lead?.id ?? null,
    source: getPendingTelecallingSource(prelead) || null,
    plotArea: prelead.plotArea ?? prelead.plot_area ?? []
  });

  const openWhatsAppConversation = (prelead) => {
    const normalized = getPreleadNormalizedPhone(prelead);
    if (!normalized) {
      toast.error('No valid phone number for this prelead.');
      return;
    }
    const projectId = resolvePendingTelecallingProjectId(prelead) || '';
    navigate(`/builder/sales/whatsapp/${normalized}`, {
      state: {
        contactName: prelead.name || '',
        leadId: prelead.lead?.id || null,
        projectId
      }
    });
  };

  const createPreleadLeadMutation = useMutation({
    mutationFn: (payload) => createLead(payload)
  });
  const unqualifyPreleadMutation = useMutation({
    mutationFn: ({ preLeadId, reason }) => markPreLeadAsUnqualified(preLeadId, reason)
  });

  const refreshPendingTelecalling = () => {
    queryClient.invalidateQueries({ queryKey: ['pendingTelecalling'] });
  };

  const createLeadFromPrelead = async (row) => {
    if (!row) return null;
    if (row.lead_id) return row.lead_id;

    // A portal-email lead takes its project from the routing rule that filed
    // it; it never went through a WhatsApp campaign to take one from.
    const isPortalEmailLead = Boolean(emailLeadPortal(row));
    const projectId =
      (isPortalEmailLead ? await fetchEmailLeadProjectId(row.pre_lead_id ?? row.id) : null) ||
      resolvePendingTelecallingProjectId(row);
    if (!Number.isFinite(projectId) || projectId <= 0) {
      throw new Error(
        isPortalEmailLead
          ? 'No project for this lead. Set a project on its rule under Integrations → Email & Inboxes → Where leads go.'
          : 'No project found for this prelead.'
      );
    }

    const mapToIdArray = (items) =>
      Array.isArray(items) ? items.map((item) => item?.id ?? item).filter(Boolean) : [];

    const listSource = String(getPendingTelecallingSource(row)).trim().toLowerCase();
    const payload = {
      id: row.id,
      pre_lead_id: row.pre_lead_id ?? row.id,
      name: row.name || `Prelead ${row.id}`,
      countryCode: row.countryCode,
      phone_number: row.phone,
      source:
        listSource === 'facebook'
          ? 'facebook'
          : listSource === 'website'
            ? 'website'
            : row.source || listSource || 'whatsapp_outbound',
      interested_projects: [projectId]
    };

    if (row.email && row.email !== '—') payload.email = row.email;
    if (row.alternate_phone_number && row.alternate_phone_number !== '—') {
      payload.alternate_phone_number = row.alternate_phone_number;
    }
    if (row.occupation) payload.occupation = row.occupation;
    if (row.reference_name) payload.reference_name = row.reference_name;
    if (row.address) payload.address = row.address;
    if (row.gender) payload.gender = row.gender;
    if (row.type) payload.type = row.type;
    if (row.expected_closure_date) payload.expected_clouser_date = row.expected_closure_date;

    const interestedAccommodationType = mapToIdArray(row.accommodations);
    if (interestedAccommodationType.length) {
      payload.interestedAccomodationType = interestedAccommodationType;
    }

    const projectAccommodationIds = mapToIdArray(row.project_accommodations);
    if (projectAccommodationIds.length) {
      payload.accomodations = projectAccommodationIds;
    }

    const plotAreaIds = mapToIdArray(row.plotArea);
    if (plotAreaIds.length) {
      payload.plotArea = plotAreaIds;
    }

    if (row.min_estimated_budget != null && row.max_estimated_budget != null) {
      payload.expectedBudgetRange = `${row.min_estimated_budget}-${row.max_estimated_budget}`;
    } else if (row.expected_budget != null && Number(row.expected_budget) > 0) {
      payload.expectedBudgetRange = String(row.expected_budget);
    }

    if (row.qualify_notes) {
      payload.qualify_notes = row.qualify_notes;
    }

    const result = await createPreleadLeadMutation.mutateAsync(payload);
    return result?.id ?? result?.data?.id ?? null;
  };

  const handleUpdateStatus = async ({ categoryKey, statusLabel, notes }) => {
    const row = statusUpdateRow;
    if (!row?.id) return;

    const noteText = formatPreleadStatusNote(categoryKey, statusLabel, notes);
    setIsUpdatingStatus(true);
    try {
      if (categoryKey === PRELEAD_STATUS_CATEGORY.CONNECTED_QUALIFIED) {
        // Create CRM lead first (qualify_notes goes on the lead). Only then write
        // the prelead activity note — avoids fake "Qualified" history when create fails.
        const leadId = await createLeadFromPrelead({
          ...row,
          qualify_notes: noteText
        });
        await addPreLeadNote(row.id, noteText);
        toast.success('Marked as Qualified.');
        refreshPendingTelecalling();
        setStatusUpdateRow(null);
        return;
      }

      if (categoryKey === PRELEAD_STATUS_CATEGORY.CONNECTED_UNQUALIFIED) {
        await unqualifyPreleadMutation.mutateAsync({ preLeadId: row.id, reason: statusLabel });
        await addPreLeadNote(row.id, noteText);
        toast.success('Marked as Unqualified.');
        refreshPendingTelecalling();
        setStatusUpdateRow(null);
        return;
      }

      await addPreLeadNote(row.id, noteText);
      toast.success('Status updated.');
      refreshPendingTelecalling();
      setStatusUpdateRow(null);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Could not update status.');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const renderPendingTelecallingAction = (prelead) => (
    <div className="flex flex-nowrap items-center justify-center gap-1.5 whitespace-nowrap">
      {renderPendingTelecallingStatusAction(prelead)}
      <button
        type="button"
        title="Edit name, phone or email"
        aria-label={`Edit ${prelead.name || 'prelead'}`}
        onClick={() => setEditRow(toPendingTelecallingRow(prelead))}
        className="inline-flex items-center justify-center rounded-lg border border-customBlue/30 bg-white p-1.5 text-customBlue hover:bg-customBlue/10 transition-colors">
        <Pencil className="h-3.5 w-3.5" />
      </button>
    </div>
  );

  const renderPendingTelecallingStatusAction = (prelead) => {
    const row = toPendingTelecallingRow(prelead);
    const currentState = String(row.state ?? '').toLowerCase();
    const isInterested = currentState === 'interested';

    if (!isInterested) {
      return (
        <button
          type="button"
          onClick={() => openPendingTelecallingList(prelead)}
          className="inline-flex items-center gap-1 rounded-lg border border-customBlue/30 bg-[#E9F5F6] px-2.5 py-1 text-xs font-semibold text-customBlue hover:bg-customBlue/20 transition-colors">
          <Eye className="h-3.5 w-3.5" />
          View
        </button>
      );
    }
    if (row.lead_id != null) {
      return <div className="text-green-700 font-semibold">Qualified</div>;
    }
    if (row.is_disqualified) {
      return <div className="text-red-700 font-semibold">Unqualified</div>;
    }
    return (
      <button
        type="button"
        onClick={() => setStatusUpdateRow(row)}
        className="inline-flex items-center whitespace-nowrap rounded-lg border border-customBlue/50 bg-[#E9F5F6] px-2.5 py-1 text-[11px] font-semibold text-customBlue hover:bg-customBlue/15 transition-colors">
        Update Status
      </button>
    );
  };

  const openAllLeadsPage = (filter = 'all') => {
    const base = isMarketing ? '/builder/marketing' : '/builder/sales';
    navigate(`${base}/prelead-lists/all-leads?filter=${encodeURIComponent(filter)}`);
  };

  const openCreateModal = () => {
    setListName('');
    if (!companyId && companies.length > 0) {
      setCompanyId(String(companies[0].id));
    }
    setIsCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
    setListName('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const id = Number(companyId);
    if (!id) {
      toast.error('Please select a company');
      return;
    }
    const name = capitalizeWords(listName.trim());
    if (!name) {
      toast.error('Please enter a list name');
      return;
    }
    submitPreLeadList({ company_id: id, name });
  };

  const totalPreLeadLists = overallStats?.total_pre_lead_lists ?? 0;
  const totalPreLeads = overallStats?.total_pre_leads ?? 0;
  const interestedCount = overallStats?.interested_count ?? overallStats?.interested ?? 0;
  const notInterestedCount =
    overallStats?.not_interested_count ?? overallStats?.not_interested ?? 0;
  const pendingRaw = overallStats?.pending_count ?? overallStats?.pending;
  const qualifiedFromPreLeads = overallStats?.qualified_from_pre_leads ?? 0;
  const campaignFailedCount = overallStats?.campaign_failed_count ?? 0;
  const campaignNotSentCount = overallStats?.campaign_pre_lead_list_not_sent ?? 0;
  const campaignSentCount = overallStats?.whatsapp_campaign_sent_pre_lead ?? 0;

  const awaitingCount = Math.max(
    0,
    Number(
      pendingRaw != null
        ? pendingRaw
        : totalPreLeads - interestedCount - notInterestedCount
    )
  );

  const showPageLoader = isListsLoading || isCompaniesPending;

  const statCards = [
    {
      label: 'Total Lists',
      value: totalPreLeadLists,
      icon: ListChecks,
      iconBg: 'bg-indigo-100',
      iconColor: 'text-indigo-600',
      valuColor: 'text-indigo-700',
      border: 'border-indigo-200'
    },
    {
      label: 'Total Leads',
      value: totalPreLeads,
      icon: Users,
      iconBg: 'bg-customBlue/10',
      iconColor: 'text-customBlue',
      valuColor: 'text-customBlue',
      border: 'border-customBlue/20',
      onClick: () => openAllLeadsPage('all')
    },
    {
      label: 'Potential Qualified',
      value: interestedCount,
      icon: TrendingUp,
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600',
      valuColor: 'text-green-700',
      border: 'border-green-200',
      onClick: () => openAllLeadsPage('potential-qualified')
    },
    {
      label: 'Qualified',
      value: qualifiedFromPreLeads,
      icon: UserCheck,
      iconBg: 'bg-teal-100',
      iconColor: 'text-teal-600',
      valuColor: 'text-teal-700',
      border: 'border-teal-200',
      onClick: () => openAllLeadsPage('qualified')
    },
    {
      label: 'Not Interested',
      value: notInterestedCount,
      icon: TrendingDown,
      iconBg: 'bg-red-100',
      iconColor: 'text-red-500',
      valuColor: 'text-red-600',
      border: 'border-red-200',
      onClick: () => openAllLeadsPage('not-interested')
    },
    {
      label: 'Awaiting Response',
      value: awaitingCount,
      icon: Clock,
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-500',
      valuColor: 'text-amber-600',
      border: 'border-amber-200',
      onClick: () => openAllLeadsPage('awaiting')
    },
    {
      label: 'Message Sent',
      value: campaignSentCount,
      icon: Send,
      iconBg: 'bg-sky-100',
      iconColor: 'text-sky-600',
      valuColor: 'text-sky-700',
      border: 'border-sky-200'
    },
    {
      label: 'Campaign Not Sent',
      value: campaignNotSentCount,
      icon: AlertCircle,
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600',
      valuColor: 'text-orange-700',
      border: 'border-orange-200'
    },
    {
      label: 'Campaign Send Failed',
      value: campaignFailedCount,
      icon: XCircle,
      iconBg: 'bg-rose-100',
      iconColor: 'text-rose-600',
      valuColor: 'text-rose-700',
      border: 'border-rose-200'
    }
  ];

  const content = (
    <div className={`bg-gray-50 w-full min-h-full ${isMarketing ? 'mt-[4.5rem]' : 'mt-[4.4rem]'}`}>
      <Loader isVisible={showPageLoader} />
      <input
        ref={quickUploadInputRef}
        type="file"
        accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
        className="hidden"
        onChange={onQuickUploadChange}
      />

      <div className="px-3 sm:px-6 py-5 space-y-6 pb-10">
        {/* Stat cards — responsive multi-row */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5 gap-3 sm:gap-4">
          {statCards.map(({ label, value, icon: Icon, iconBg, iconColor, valuColor, border, onClick }) => {
            const baseClassName = `rounded-xl border ${border} bg-white px-4 py-3 sm:px-5 sm:py-4 shadow-sm flex items-center gap-3`;
            if (onClick) {
              return (
                <button
                  key={label}
                  type="button"
                  onClick={onClick}
                  className={`${baseClassName} relative pr-10 text-left transition-colors hover:bg-[#E9F5F6] focus:outline-none focus-visible:ring-2 focus-visible:ring-customBlue/30`}>
                  <div className={`hidden sm:flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${iconBg}`}>
                    <Icon className={`h-5 w-5 ${iconColor}`} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide leading-tight">{label}</p>
                    <p className={`mt-0.5 text-2xl sm:text-3xl font-bold leading-none ${valuColor}`}>{value}</p>
                  </div>
                  <span
                    className="absolute bottom-2 right-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-customBlue/10 text-customBlue"
                    aria-hidden>
                    <ArrowUpRight className="h-3.5 w-3.5" />
                  </span>
                </button>
              );
            }

            return (
              <div key={label} className={baseClassName}>
                <div className={`hidden sm:flex h-11 w-11 shrink-0 items-center justify-center rounded-lg ${iconBg}`}>
                  <Icon className={`h-5 w-5 ${iconColor}`} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide leading-tight">{label}</p>
                  <p className={`mt-0.5 text-2xl sm:text-3xl font-bold leading-none ${valuColor}`}>{value}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Tabs: All Lists | Pending Telecalling (+ company/search on same row) */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-4 sm:gap-6">
            <button
              type="button"
              onClick={() => setActiveListTab('all-lists')}
              className={`flex items-center gap-2 text-sm font-semibold transition-colors ${
                activeListTab === 'all-lists'
                  ? 'text-gray-800'
                  : 'text-gray-400 hover:text-gray-600'
              }`}>
              All Lists
              {!isListsLoading && !isListsError && (
                <span
                  className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                    activeListTab === 'all-lists'
                      ? 'bg-gray-100 text-gray-700'
                      : 'bg-gray-50 text-gray-400'
                  }`}>
                  {sortedLists.length}
                </span>
              )}
            </button>

            <button
              type="button"
              onClick={() => setActiveListTab('pending-telecalling')}
              className={`flex items-center gap-2 text-sm font-semibold transition-colors ${
                activeListTab === 'pending-telecalling'
                  ? 'text-gray-800'
                  : 'text-gray-400 hover:text-gray-600'
              }`}>
              Pending Telecalling
              {telecallingCompanyId && !isPendingTelecallingError && (
                <span
                  className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                    activeListTab === 'pending-telecalling'
                      ? 'bg-gray-100 text-gray-700'
                      : 'bg-gray-50 text-gray-400'
                  }`}>
                  {isPendingTelecallingLoading && pendingTelecallingRows.length === 0
                    ? '…'
                    : (pendingTelecallingMeta.total ?? pendingTelecallingRows.length)}
                </span>
              )}
            </button>
          </div>

          {activeListTab === 'pending-telecalling' && (
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={telecallingCompanyId}
                onChange={(e) => {
                  const next = e.target.value;
                  setTelecallingCompanyId(next);
                  setTelecallingPage(1);
                  if (next) {
                    localStorage.setItem('companyId', next);
                  }
                }}
                className="min-w-[10rem] border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-customBlue/30 focus:border-customBlue bg-white">
                <option value="">Select company</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.company_name || company.name || `Company ${company.id}`}
                  </option>
                ))}
              </select>

              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                <input
                  type="search"
                  value={telecallingSearchInput}
                  onChange={(e) => setTelecallingSearchInput(e.target.value)}
                  placeholder="Search contacts…"
                  disabled={!telecallingCompanyId}
                  className="w-44 sm:w-56 border border-gray-300 rounded-lg pl-8 pr-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-customBlue/30 focus:border-customBlue disabled:bg-gray-50 disabled:text-gray-400"
                />
              </div>
            </div>
          )}
        </div>

        {activeListTab === 'all-lists' && (
          <>
            {isListsError && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                {listsError?.response?.data?.message || listsError?.message || 'Could not load lists.'}
              </div>
            )}

            {/* Empty state */}
            {!isListsLoading && !isListsError && sortedLists.length === 0 && (
              <div className="w-full max-w-2xl rounded-2xl border border-customBlue/20 bg-white p-6 shadow-sm">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="prelead-inline-company" className="block text-sm font-medium text-gray-700 mb-1">
                      Company
                    </label>
                    <select
                      id="prelead-inline-company"
                      value={companyId}
                      onChange={(e) => {
                        const next = e.target.value;
                        setCompanyId(next);
                        if (next) {
                          localStorage.setItem('companyId', next);
                        }
                      }}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-customBlue/30 focus:border-customBlue">
                      <option value="">Select company</option>
                      {companies.map((company) => (
                        <option key={company.id} value={company.id}>
                          {company.company_name || company.name || `Company ${company.id}`}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="prelead-inline-name" className="block text-sm font-medium text-gray-700 mb-1">
                      List name
                    </label>
                    <input
                      id="prelead-inline-name"
                      type="text"
                      value={listName}
                      onChange={(e) => setListName(capitalizeWords(e.target.value))}
                      placeholder="e.g. Sept Campaign Contacts"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-customBlue/30 focus:border-customBlue"
                    />
                  </div>
                  <div className="pt-2 flex justify-start">
                    <Button type="submit" disabled={isCreatePending}>
                      {isCreatePending ? 'Creating…' : 'Create list'}
                    </Button>
                  </div>
                </form>
              </div>
            )}

            {/* List grid — shown when lists exist (includes Create card as first item) */}
            {!isListsLoading && !isListsError && sortedLists.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 3xl:grid-cols-4 gap-3 sm:gap-4">
                {/* Create a list — first card */}
                <button
                  type="button"
                  onClick={openCreateModal}
                  className="group flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-customBlue/40 bg-white px-4 py-8 text-customBlue shadow-sm hover:border-customBlue hover:bg-[#E9F5F6] transition-all duration-200 min-h-[160px]">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-customBlue/10 group-hover:bg-customBlue/20 transition-colors">
                    <Plus className="h-5 w-5" />
                  </span>
                  <span className="text-sm font-semibold whitespace-nowrap">Create a list</span>
                </button>

                {sortedLists.map((list) => {
                  const busy =
                    downloadingId === list.id ||
                    uploadingId === list.id ||
                    (isDeletePending && pendingDeleteList?.id === list.id);
                  const companyName = companyNameById.get(list.company_id) ?? `ID ${list.company_id}`;
                  const contactCount = list.pre_lead_count ?? 0;
                  return (
                    <article
                      key={list.id}
                      className="group relative rounded-xl border border-gray-200 bg-white shadow-sm hover:shadow-md hover:border-customBlue/40 transition-all duration-200 flex flex-col overflow-hidden">
                      {/* Card top accent */}
                      <div className="h-1 w-full bg-gradient-to-r from-customBlue to-customBlue/50 rounded-t-xl" />

                      {canDeletePreleads && (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={(e) => {
                            e.stopPropagation();
                            setPendingDeleteList(list);
                          }}
                          title="Delete list"
                          aria-label={`Delete list ${list.name}`}
                          className="absolute right-2 top-3 z-10 inline-flex h-7 w-7 items-center justify-center rounded-md border border-transparent text-gray-400 opacity-70 transition-all hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600 hover:opacity-100 disabled:opacity-40 group-hover:opacity-100">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}

                      <div className="p-4 flex-1 flex flex-col gap-3 pr-10">
                        {/* Name */}
                        <h3
                          className="text-sm font-semibold text-gray-900 leading-snug line-clamp-2 group-hover:text-customBlue transition-colors cursor-pointer"
                          onClick={() => openListDetailPage(list)}>
                          {list.name}
                        </h3>

                        {/* Meta */}
                        <div className="space-y-1.5">
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <Building2 className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                            <span className="truncate font-medium text-gray-700" title={companyName}>
                              {companyName}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <Calendar className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                            <span>{formatListDate(list.created_at)}</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <Users className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                            <span className="font-medium text-gray-700">
                              {contactCount} contact{contactCount !== 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="border-t border-gray-100 px-3 py-2.5 flex gap-1.5">
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => openListDetailPage(list)}
                          title="View"
                          className="inline-flex flex-1 items-center justify-center gap-1 rounded-lg border border-customBlue/30 bg-[#E9F5F6] px-2 py-1.5 text-xs font-semibold text-customBlue hover:bg-customBlue/20 disabled:opacity-50 transition-colors">
                          <Eye className="h-3.5 w-3.5 shrink-0" />
                          <span>View</span>
                        </button>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => runDownload(list.id, list.name)}
                          title="Download template"
                          className="inline-flex flex-1 items-center justify-center gap-1 rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors">
                          <Download className="h-3.5 w-3.5 shrink-0" />
                          <span className="hidden sm:inline">
                            {downloadingId === list.id ? '…' : 'Download'}
                          </span>
                          {downloadingId === list.id && <span className="sm:hidden">…</span>}
                        </button>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => openQuickUploadPicker(list.id)}
                          title="Upload contacts"
                          className="inline-flex flex-1 items-center justify-center gap-1 rounded-lg bg-customBlue px-2 py-1.5 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-50 transition-opacity">
                          <Upload className="h-3.5 w-3.5 shrink-0" />
                          <span>{uploadingId === list.id ? '…' : 'Upload'}</span>
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </>
        )}

        {activeListTab === 'pending-telecalling' && (
          <div className="space-y-3">
            {!telecallingCompanyId && (
              <div className="rounded-lg border border-dashed border-gray-200 bg-white px-4 py-8 text-center text-sm text-gray-500">
                Select a company to view pending telecalling contacts.
              </div>
            )}

            {telecallingCompanyId && isPendingTelecallingError && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                {pendingTelecallingError?.response?.data?.message ||
                  pendingTelecallingError?.message ||
                  'Could not load pending telecalling.'}
              </div>
            )}

            {telecallingCompanyId && !isPendingTelecallingError && (
              <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-[#E9F5F6] text-customBlue">
                      <th className="px-3 py-2.5 text-left font-semibold whitespace-nowrap border-b border-customBlue/15">
                        Name
                      </th>
                      <th className="px-3 py-2.5 text-left font-semibold whitespace-nowrap border-b border-customBlue/15">
                        Phone
                      </th>
                      <th className="px-3 py-2.5 text-left font-semibold whitespace-nowrap border-b border-customBlue/15">
                        Source
                      </th>
                      <th className="px-3 py-2.5 text-left font-semibold whitespace-nowrap border-b border-customBlue/15">
                        Status
                      </th>
                      <th className="px-3 py-2.5 text-left font-semibold whitespace-nowrap border-b border-customBlue/15">
                        Assigned
                      </th>
                      <th className="px-3 py-2.5 text-left font-semibold whitespace-nowrap border-b border-customBlue/15">
                        Created
                      </th>
                      <th className="px-3 py-2.5 text-left font-semibold whitespace-nowrap border-b border-customBlue/15">
                        Response
                      </th>
                      <th className="px-3 py-2.5 text-left font-semibold whitespace-nowrap border-b border-customBlue/15">
                        Notes
                      </th>
                      {!isMarketing && (
                        <th className="w-12 px-1.5 py-2.5 text-center font-semibold whitespace-nowrap border-b border-customBlue/15">
                          WA
                        </th>
                      )}
                      <th className="px-3 py-2.5 text-center font-semibold whitespace-nowrap border-b border-customBlue/15">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {isPendingTelecallingLoading && pendingTelecallingRows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={isMarketing ? 9 : 10}
                          className="px-3 py-8 text-center text-sm text-gray-500">
                          Loading…
                        </td>
                      </tr>
                    ) : pendingTelecallingRows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={isMarketing ? 9 : 10}
                          className="px-3 py-8 text-center text-sm text-gray-500">
                          {telecallingSearch
                            ? `No pending telecalling contacts matching “${telecallingSearch}”.`
                            : 'No pending telecalling contacts for this company.'}
                        </td>
                      </tr>
                    ) : (
                      pendingTelecallingRows.map((prelead) => {
                        const phoneDisplay =
                          [prelead.countryCode, prelead.phone].filter(Boolean).join(' ').trim() || '—';
                        const sourceLabel = getPendingTelecallingSource(prelead);
                        const assignedLabel = prelead.sales?.name?.trim() || 'No Assigned';
                        const rowForNotes = toPendingTelecallingRow(prelead);
                        const normalizedPhone = getPreleadNormalizedPhone(prelead);
                        return (
                          <tr key={prelead.id} className="hover:bg-gray-50/80">
                            <td className="px-3 py-2 text-gray-800 max-w-[10rem] sm:max-w-[14rem]">
                              <span className="inline-flex items-center gap-2 min-w-0">
                                <PreleadSlaIndicator slaColor={prelead.sla_color} />
                                <span
                                  className="block truncate"
                                  title={prelead.name ? String(prelead.name) : undefined}>
                                  {prelead.name ? capitalizeWords(String(prelead.name)) : '—'}
                                </span>
                              </span>
                            </td>
                            <td className="px-3 py-2 text-gray-800 whitespace-nowrap">{phoneDisplay}</td>
                            <td className="px-3 py-2 text-gray-800 whitespace-nowrap">
                              {sourceLabel ? formatStatusLabel(String(sourceLabel)) : '—'}
                            </td>
                            <td className="px-3 py-2 text-gray-800 whitespace-nowrap">
                              {prelead.state ? formatStatusLabel(String(prelead.state)) : '—'}
                            </td>
                            <td
                              className="px-3 py-2 text-gray-800 max-w-[8rem] truncate whitespace-nowrap"
                              title={assignedLabel}>
                              {assignedLabel}
                            </td>
                            <td className="px-3 py-2 text-gray-800 whitespace-nowrap">
                              {formatListDate(prelead.created_at)}
                            </td>
                            <td className="px-3 py-2">
                              {renderPendingTelecallingResponseCell(prelead, {
                                onFacebook: setSelectedPreleadForMetaFields,
                                onGoogleAds: setSelectedPreleadForGoogleAdsFields,
                                onWebsite: setSelectedPreleadForWebsiteFields,
                                onEmailLead: setSelectedPreleadForEmailLeadFields,
                                onWhatsAppFlow: setSelectedPreleadForAnswers
                              })}
                            </td>
                            <td className="px-3 py-2">
                              <PreleadNoteButton
                                row={rowForNotes}
                                onClick={() => setSelectedPreleadForLogs(rowForNotes)}
                              />
                            </td>
                            {!isMarketing && (
                              <td className="w-12 px-1.5 py-2 text-center">
                                {normalizedPhone ? (
                                  <button
                                    type="button"
                                    title="Open WhatsApp conversation"
                                    aria-label="Open WhatsApp conversation"
                                    onClick={() => openWhatsAppConversation(prelead)}
                                    className="relative inline-flex items-center justify-center rounded-full p-1 text-green-600 transition-colors hover:bg-green-50">
                                    <MessageCircle className="h-4 w-4" strokeWidth={2} />
                                  </button>
                                ) : (
                                  <span className="text-gray-300">—</span>
                                )}
                              </td>
                            )}
                            <td className="px-3 py-2 text-center">
                              {isMarketing ? (
                                <button
                                  type="button"
                                  onClick={() => openPendingTelecallingList(prelead)}
                                  className="inline-flex items-center gap-1 rounded-lg border border-customBlue/30 bg-[#E9F5F6] px-2.5 py-1 text-xs font-semibold text-customBlue hover:bg-customBlue/20 transition-colors">
                                  <Eye className="h-3.5 w-3.5" />
                                  View
                                </button>
                              ) : (
                                renderPendingTelecallingAction(prelead)
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {telecallingCompanyId &&
              !isPendingTelecallingError &&
              Number(pendingTelecallingMeta.totalPages) > 1 && (
                <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
                  <PaginationComponent
                    page={Number(pendingTelecallingMeta.page) || telecallingPage}
                    totalPages={Number(pendingTelecallingMeta.totalPages) || 1}
                    onPageChange={(nextPage) => {
                      const totalPages = Number(pendingTelecallingMeta.totalPages) || 1;
                      const boundedPage = Math.max(1, Math.min(totalPages, Number(nextPage)));
                      setTelecallingPage(boundedPage);
                    }}
                    isFetching={isPendingTelecallingFetching}
                  />
                </div>
              )}
          </div>
        )}
      </div>

      <UploadResultsModal
        isOpen={isUploadResultModalOpen}
        onClose={closeUploadResultModal}
        uploadSuccesses={uploadSuccesses}
        uploadErrors={uploadErrors}
        uploadDuplicates={uploadDuplicates}
        onAcceptDuplicates={() => {}}
        onRejectDuplicates={handleRejectPreleadDuplicates}
        isProcessingDuplicates={false}
        onRetryUpload={handleRetryPreleadUpload}
        fileEntityLabel="preleads"
        duplicateActionsEnabled={false}
      />

      {isCreateModalOpen && (
        <Modal name="Create prelead list" onClose={closeCreateModal} width={40}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="prelead-list-company" className="block text-sm font-medium text-gray-700 mb-1">
                Company
              </label>
              <select
                id="prelead-list-company"
                value={companyId}
                onChange={(e) => {
                  const next = e.target.value;
                  setCompanyId(next);
                  if (next) {
                    localStorage.setItem('companyId', next);
                  }
                }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-customBlue/30 focus:border-customBlue">
                <option value="">Select company</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.company_name || company.name || `Company ${company.id}`}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="prelead-list-name" className="block text-sm font-medium text-gray-700 mb-1">
                List name
              </label>
              <input
                id="prelead-list-name"
                type="text"
                value={listName}
                onChange={(e) => setListName(capitalizeWords(e.target.value))}
                placeholder="e.g. Sept Campaign Contacts"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-customBlue/30 focus:border-customBlue"
              />
            </div>
            <div className="pt-2 flex justify-end gap-3">
              <Button type="button" displayType="secondary" onClick={closeCreateModal}>
                Cancel
              </Button>
              <Button type="submit" disabled={isCreatePending}>
                {isCreatePending ? 'Creating…' : 'Create'}
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {pendingDeleteList && (
        <Modal
          name="Delete prelead list"
          onClose={() => {
            if (isDeletePending) return;
            setPendingDeleteList(null);
          }}
          width={40}>
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              Delete <span className="font-semibold text-gray-900">{pendingDeleteList.name}</span>?
              {(pendingDeleteList.pre_lead_count ?? 0) > 0
                ? ` This will remove ${pendingDeleteList.pre_lead_count} contact${
                    pendingDeleteList.pre_lead_count === 1 ? '' : 's'
                  } from this list. Converted leads stay in Sales.`
                : ' This cannot be undone.'}
            </p>
            <div className="flex justify-end gap-3 pt-1">
              <Button
                type="button"
                displayType="secondary"
                disabled={isDeletePending}
                onClick={() => setPendingDeleteList(null)}>
                Cancel
              </Button>
              <Button
                type="button"
                displayType="primary-red"
                disabled={isDeletePending}
                onClick={() => removePreLeadList(pendingDeleteList.id)}>
                {isDeletePending ? 'Deleting…' : 'Delete list'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
      {selectedPreleadForLogs && (
        <PreleadLogModal
          prelead={selectedPreleadForLogs}
          onClose={() => setSelectedPreleadForLogs(null)}
          onLogAdded={() => {
            queryClient.invalidateQueries({ queryKey: ['pendingTelecalling'] });
          }}
        />
      )}
      {selectedPreleadForAnswers && (
        <WhatsAppFlowAnswersModal
          selectedPrelead={selectedPreleadForAnswers}
          onClose={() => setSelectedPreleadForAnswers(null)}
        />
      )}
      {selectedPreleadForMetaFields && (
        <FacebookMetaFieldsModal
          preLeadId={Number(selectedPreleadForMetaFields.id)}
          prelead={selectedPreleadForMetaFields}
          onClose={() => setSelectedPreleadForMetaFields(null)}
        />
      )}
      {selectedPreleadForGoogleAdsFields && (
        <GoogleAdsFieldsModal
          preLeadId={Number(selectedPreleadForGoogleAdsFields.id)}
          prelead={selectedPreleadForGoogleAdsFields}
          onClose={() => setSelectedPreleadForGoogleAdsFields(null)}
        />
      )}
      {selectedPreleadForWebsiteFields && (
        <WebsiteFormFieldsModal
          preLeadId={Number(selectedPreleadForWebsiteFields.id)}
          prelead={selectedPreleadForWebsiteFields}
          onClose={() => setSelectedPreleadForWebsiteFields(null)}
        />
      )}
      {selectedPreleadForEmailLeadFields && (
        <EmailLeadFieldsModal
          preLeadId={Number(selectedPreleadForEmailLeadFields.id)}
          prelead={selectedPreleadForEmailLeadFields}
          onClose={() => setSelectedPreleadForEmailLeadFields(null)}
        />
      )}
      {editRow ? <EditPreleadModal prelead={editRow} onClose={() => setEditRow(null)} /> : null}

      <PreleadUpdateStatusModal
        open={Boolean(statusUpdateRow)}
        prelead={statusUpdateRow}
        isSubmitting={
          isUpdatingStatus ||
          createPreleadLeadMutation.isPending ||
          unqualifyPreleadMutation.isPending
        }
        onClose={() => {
          if (
            isUpdatingStatus ||
            createPreleadLeadMutation.isPending ||
            unqualifyPreleadMutation.isPending
          ) {
            return;
          }
          setStatusUpdateRow(null);
        }}
        onConfirm={handleUpdateStatus}
      />
    </div>
  );

  if (isMarketing) {
    return (
      <Layout>
        <div
          className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${
            collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
          }`}>
          <Header data={{ name: 'Marketing > Prelead List' }} />
        </div>
        <div className={`transition-all duration-300 ease-in-out ${collapsed ? 'ml-[1vw]' : ''}`}>
          {content}
        </div>
      </Layout>
    );
  }

  return content;
};

export default PreleadList;
